"use client";

import React, { useEffect, useState } from 'react';
import { useCookieConsent } from '@/context/CookieConsentContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { X, CheckCircle2, Circle, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';

const CookieConsent: React.FC = () => {
    const { cookies, consent, showConsent, setShowConsent, acceptCookie, rejectCookie, acceptAll } = useCookieConsent();
    const [isOpen, setIsOpen] = useState(false);
    const [expandedCookies, setExpandedCookies] = useState<Set<number>>(new Set());

    useEffect(() => {
        setIsOpen(showConsent);
    }, [showConsent]);

    const toggleExpanded = (id: number) => {
        const newExpanded = new Set(expandedCookies);
        if (newExpanded.has(id)) {
            newExpanded.delete(id);
        } else {
            newExpanded.add(id);
        }
        setExpandedCookies(newExpanded);
    };

    if (!isOpen || cookies.length === 0) {
        return null;
    }

    return (
        <>
            {/* Overlay */}
            <div className="fixed inset-0 bg-black/50 z-40 pointer-events-auto" onClick={() => setIsOpen(false)} />

            {/* Modal - Jobb oldali ablak */}
            <div
                className={cn(
                    'fixed right-0 top-0 h-screen w-full max-w-md bg-card border-l border-border shadow-2xl overflow-y-auto z-50',
                    'transform transition-transform duration-300',
                    isOpen ? 'translate-x-0' : 'translate-x-full'
                )}
            >
                <div className="sticky top-0 flex items-center justify-between p-4 border-b border-border bg-card">
                    <h2 className="text-lg font-bold text-primary">🍪 Cookie Kezelés</h2>
                    <button
                        onClick={() => setIsOpen(false)}
                        className="p-1 hover:bg-accent rounded-md transition-colors"
                        aria-label="Bezárás"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-4 space-y-4">
                    {/* Leírás */}
                    <div className="text-sm text-muted-foreground">
                        <p>
                            A weboldal sütiket használ a felhasználói élmény javítása és az oldal működésének biztosítása
                            érdekében. Az alábbi kategóriákban választhatsz:
                        </p>
                    </div>

                    {/* Cookie Lista */}
                    <div className="space-y-3 max-h-[calc(100vh-400px)] overflow-y-auto">
                        {cookies.map((cookie) => {
                            const isAccepted = consent[cookie.key] === true;
                            const isRejected = cookie.key in consent && consent[cookie.key] === false;
                            const isExpanded = expandedCookies.has(cookie.id);

                            return (
                                <Card key={cookie.id} className={cn('bg-background border', isAccepted && 'border-green-500')}>
                                    <CardContent className="pt-4">
                                        <div className="space-y-2">
                                            {/* Header */}
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="flex-1">
                                                    <h3 className="font-semibold text-sm">{cookie.name}</h3>
                                                    <p className="text-xs text-muted-foreground capitalize">{cookie.category}</p>
                                                </div>
                                                {isAccepted && <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-1" />}
                                                {isRejected && <Circle className="w-4 h-4 text-red-500 flex-shrink-0 mt-1" />}
                                            </div>

                                            {/* Leírás */}
                                            <p className="text-xs text-muted-foreground">{cookie.description}</p>

                                            {/* Megőrzési idő */}
                                            {cookie.retention && (
                                                <p className="text-xs text-muted-foreground">
                                                    <strong>Megőrzés:</strong> {cookie.retention}
                                                </p>
                                            )}

                                            {/* Expandable Tartalom */}
                                            {cookie.content && (
                                                <>
                                                    <button
                                                        onClick={() => toggleExpanded(cookie.id)}
                                                        className="text-xs text-primary hover:underline flex items-center gap-1 w-full justify-center py-1"
                                                    >
                                                        {isExpanded ? (
                                                            <>
                                                                <ChevronUp className="w-3 h-3" /> Csukás
                                                            </>
                                                        ) : (
                                                            <>
                                                                <ChevronDown className="w-3 h-3" /> Részletek
                                                            </>
                                                        )}
                                                    </button>

                                                    {isExpanded && (
                                                        <div className="text-xs text-muted-foreground bg-muted/30 p-2 rounded-md prose prose-sm max-w-none dark:prose-invert">
                                                            <div
                                                                dangerouslySetInnerHTML={{ __html: cookie.content }}
                                                                className="text-xs text-muted-foreground"
                                                            />
                                                        </div>
                                                    )}
                                                </>
                                            )}

                                            {/* Gombok */}
                                            <div className="flex gap-2 pt-2">
                                                <Button
                                                    size="sm"
                                                    variant={isAccepted ? 'default' : 'outline'}
                                                    onClick={() => acceptCookie(cookie.key)}
                                                    className="flex-1"
                                                >
                                                    Elfogad
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant={isRejected || (!isAccepted && !(cookie.key in consent)) ? 'destructive' : 'outline'}
                                                    onClick={() => rejectCookie(cookie.key)}
                                                    className="flex-1"
                                                >
                                                    Elutasít
                                                </Button>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>

                    {/* Alap gombok */}
                    <div className="flex flex-col gap-2 pt-4 border-t border-border sticky bottom-0 bg-card py-4">
                        <Button onClick={acceptAll} className="w-full">
                            ✓ Mindent Elfogad
                        </Button>
                        <Button variant="outline" onClick={() => setIsOpen(false)} className="w-full">
                            Bezárás
                        </Button>
                    </div>
                </div >
            </div >
        </>
    );
};

export default CookieConsent;
